import { useCallback, useMemo, useState } from 'react';
import { RowLink, ROW_LINK_HOST } from '../components/links/EntityLink';
import {
  makeStyles,
  mergeClasses,
  tokens,
  Text,
  Spinner,
  MessageBar,
  MessageBarBody,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TableCellLayout,
  Input,
  Field,
  Badge,
  Avatar,
  Switch,
  Checkbox,
  Button,
} from '@fluentui/react-components';
import {
  SearchRegular,
  BuildingRegular,
  TagMultipleRegular,
  ArrowSortRegular,
  ArrowSortUpRegular,
  ArrowSortDownRegular,
  BookRegular,
  EditRegular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { QuickViewPills } from '../components/listFilters/QuickViewPills';
import { CLIENT_PRESETS, DEFAULT_CLIENT_PRESET, activeClientPreset, clientPresetState } from '../utils/clientPresets';
import BulkEditClientsDialog from '../components/state-tax/BulkEditClientsDialog';
import ColumnHeaderFilter from '../components/common/ColumnHeaderFilter';
import { LookupColumnFilter } from '../components/listFilters/LookupColumnFilter';
import {
  EMPTY_SELECTION, applyLookups, facetCountsFor, isLookupActive, type LookupColumn, type LookupSelection,
} from '../components/listFilters/lookupFilter';
import {
  useStateTaxClients,
  useObligationTaxTypesByClient,
  useFilingHealthByClient,
  useBookkeepingAccountIds,
  useClientsNextDue,
  useClientBillingSummaries,
  useSalesTaxAdmins,
} from '../services/stateTaxClientsApi';
import { monthsAgo } from '../utils/dateMath';
import {
  expectedPeriodMonths,
  freshnessFor,
  freshnessSortKey,
  nextDueSortKey,
  includeTestClients,
  isTestClient,
} from '../utils/obligationHelpers';
import type { ClientBilling, FilingHealthSummary, StateTaxClientSummary } from '../types';
import { apiErrorMessage } from '../lib/apiErrorMessage';
import SecondaryLoadWarning from '../components/common/SecondaryLoadWarning';
import { formatWholeDay, wholeDayLocalDate } from '../utils/complianceFormatters';
import { pageShell } from '../components/layout/pageShell';
import { appLocale } from '../utils/appLocale';

const useStyles = makeStyles({
  viewsRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
  },
  viewsNote: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  page: {
    ...pageShell,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalL,
    paddingBottom: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  titleGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  title: {
    fontSize: tokens.fontSizeHero800,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: '1.1',
    margin: 0,
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
  },
  countCallout: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    borderRadius: tokens.borderRadiusMedium,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
  },
  searchField: {
    minWidth: '320px',
  },
  emptyState: {
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  tableCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    // STX-UX-01: was `hidden`; horizontal scroll keeps header sort buttons + filter
    // triggers inside their cells on narrow viewports instead of clipping them.
    overflowX: 'auto',
  },
  table: { minWidth: '1200px' },
  tableHeader: {
    backgroundColor: tokens.colorNeutralBackground2,
  },
  headerCell: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  // A real link (fleet ruling 2026-10-29) that reads as the text it replaced: underlined on hover only.
  rowLinkText: {
    color: 'inherit',
    textDecorationLine: 'none',
    ':hover': { textDecorationLine: 'underline' },
  },
  clientCell: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
  ownerCell: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  ownerName: {
    fontSize: tokens.fontSizeBase300,
  },
  badgeRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    alignItems: 'center',
  },
  // A block that fills its cell, so the ellipsis applies (on an inline span the width rules did nothing, and long
  // notes ran past the table's right edge). The whole note is in the title.
  notesCell: {
    display: 'block',
    maxWidth: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  mutedCell: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    fontSize: tokens.fontSizeBase200,
  },
  clickableRow: {
    cursor: 'pointer',
    transition: 'background-color 80ms ease-in',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  unconfiguredBadge: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    fontSize: tokens.fontSizeBase200,
  },
  // Sortable header — clickable wrapper around the label.
  sortableHeader: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
    color: 'inherit',
    font: 'inherit',
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    '&:hover': {
      color: tokens.colorNeutralForeground1,
    },
  },
  sortIcon: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
  },
  sortIconActive: {
    color: tokens.colorBrandForeground1,
  },
  // STX-UX-01 — header cell wrapper: label/sort button + the column's filter trigger.
  headerFlex: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2px',
  },
  // Long option lists (owners, tax types) scroll inside the header filter panel.
  panelScroll: {
    maxHeight: '260px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  // Last-filed badge — shows the most recent filed month + a freshness color.
  // Green = filed in the last 2 months, amber = 2–4 months, red = 4+ months or never.
  freshnessFresh: {
    color: tokens.colorPaletteGreenForeground2,
    backgroundColor: tokens.colorPaletteGreenBackground1,
    border: `1px solid ${tokens.colorPaletteGreenBorder1}`,
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontVariantNumeric: 'tabular-nums',
  },
  freshnessStale: {
    color: tokens.colorPaletteDarkOrangeForeground1,
    backgroundColor: tokens.colorPaletteDarkOrangeBackground1,
    border: `1px solid ${tokens.colorPaletteDarkOrangeBorder1}`,
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontVariantNumeric: 'tabular-nums',
  },
  freshnessOverdue: {
    color: tokens.colorPaletteRedForeground1,
    backgroundColor: tokens.colorPaletteRedBackground1,
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontVariantNumeric: 'tabular-nums',
  },
  freshnessUnknown: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    fontSize: tokens.fontSizeBase200,
  },
  // Filter row sits below the page header, above the table — search now
  // shares the row with toggle filters (currently just "Bookkeeping only").
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
    flexWrap: 'wrap',
  },
  clientNameStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  // Bulk action bar — appears above the table once one or more rows are selected.
  // Mirrors the dashboard at-risk panel's bulk bar.
  bulkBar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorBrandBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  bulkBarCount: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForeground1,
  },
  bulkBarSpacer: {
    flex: 1,
  },
  checkboxCell: {
    width: '36px',
    paddingRight: 0,
  },
});

// Map a tax type to a Fluent Badge color. Sales Tax (the most common) gets a neutral
// brand color; less-common obligations get distinct hues so a row with multiple types
// reads at a glance.
type FluentBadgeColor = 'brand' | 'severe' | 'warning' | 'success' | 'informative' | 'subtle' | 'danger' | 'important';
function badgeColorForTaxType(taxType: string): FluentBadgeColor {
  const t = taxType.toLowerCase();
  if (t.includes('sales tax')) return 'brand';
  if (t.includes('documentary stamp') || t.includes('doc stamp')) return 'severe';
  if (t.includes('solid waste')) return 'warning';
  if (t.includes('communication')) return 'informative';
  if (t.includes('tangible')) return 'success';
  if (t.includes('gross receipts')) return 'important';
  if (t.includes('fuel')) return 'danger';
  return 'subtle';
}

// Compact label for a tax type — strips "Filing" / "Fees and Surcharge" / "Tax" suffixes
// so badges stay legible. "Documentary Stamp Tax Filing" → "Doc Stamp"; "Solid Waste Fees
// and Surcharge Tax" → "Solid Waste". Sales Tax stays as-is. Falls back to the full label.
function shortTaxLabel(taxType: string): string {
  if (/sales\s*tax/i.test(taxType)) return 'Sales Tax';
  if (/documentary\s*stamp|doc\s*stamp/i.test(taxType)) return 'Doc Stamp';
  if (/solid\s*waste/i.test(taxType)) return 'Solid Waste';
  if (/communication/i.test(taxType)) return 'Comm Services';
  if (/tangible/i.test(taxType)) return 'TPP';
  if (/gross\s*receipts/i.test(taxType)) return 'Gross Receipts';
  if (/fuel/i.test(taxType)) return 'Fuel';
  if (/reemployment/i.test(taxType)) return 'Reemployment';
  return taxType;
}

// Build initials for the owner avatar (e.g. "Mabel Diaz" → "MD"). Returns null when
// no owner is set so the cell can render an "Unassigned" muted text instead.
function ownerInitials(name: string | null | undefined): string | null {
  if (!name) return null;
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return null;
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase() || null;
}


type SortColumn = 'client' | 'taxes' | 'owner' | 'lastFiled' | 'nextDue';
type SortDirection = 'asc' | 'desc';

// A preset never matches an owner selection that excludes or asks for "(Has no value)"; this stands in for one in that comparison.
const CUSTOM_OWNER_SELECTION = '\u0000custom';

// Clickable header cell that surfaces the active sort + direction. The active
// column shows a brand-tinted up/down arrow; inactive columns show a faded
// neutral icon as an affordance.
function SortableHeader({
  label, column, activeColumn, direction, onClick,
}: {
  label: string;
  column: SortColumn;
  activeColumn: SortColumn;
  direction: SortDirection;
  onClick: () => void;
}) {
  const styles = useStyles();
  const active = column === activeColumn;
  const Icon = active
    ? (direction === 'asc' ? ArrowSortUpRegular : ArrowSortDownRegular)
    : ArrowSortRegular;
  return (
    <button type="button" className={styles.sortableHeader} onClick={onClick}>
      {label}
      <Icon className={mergeClasses(styles.sortIcon, active && styles.sortIconActive)} />
    </button>
  );
}

// Pill that shows the soonest unfiled task's due date. "Filing timely is a
// priority" per CV — the column lets staff sort the roster by who's most-imminent
// and spot overdue clients at a glance. Color reuses the freshness palette:
// red when overdue, amber when due in ≤7 days, green when further out.
function NextDueBadge({
  dueMs,
  dash,
  t,
}: {
  dueMs: number | undefined;
  dash: string;
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  const styles = useStyles();
  if (dueMs === undefined) return <span className={styles.freshnessUnknown}>{dash}</span>;
  // Day-precision difference. Time-of-day on the due date doesn't matter for
  // staff intuition — "due tomorrow" should read the same at 9 AM and 4 PM.
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueMs);
  due.setHours(0, 0, 0, 0);
  const days = Math.round((due.getTime() - today.getTime()) / 86_400_000);
  const dateLabel = due.toLocaleDateString(appLocale(), { month: 'short', day: 'numeric' });
  let cls: string;
  let text: string;
  if (days < 0) {
    cls = styles.freshnessOverdue;
    text = t('clientsPage.nextDue.overdue', { date: dateLabel, count: Math.abs(days) });
  } else if (days <= 7) {
    cls = styles.freshnessStale;
    text = days === 0
      ? t('clientsPage.nextDue.today', { date: dateLabel })
      : t('clientsPage.nextDue.inDays', { date: dateLabel, count: days });
  } else {
    cls = styles.freshnessFresh;
    text = t('clientsPage.nextDue.inDays', { date: dateLabel, count: days });
  }
  return <span className={cls}>{text}</span>;
}

// Pill that shows when a client last filed. Coloring scaled to the client's
// filing cadence (Monthly stale at 2mo, Quarterly stale at 4mo, etc.) so a
// quarterly client who filed Q4 in January isn't flagged red just because
// it's now April. The pill text is the actual month; color carries urgency.
function FreshnessBadge({
  health,
  frequency,
}: {
  health: FilingHealthSummary | undefined;
  frequency: string | null | undefined;
}) {
  const styles = useStyles();
  const { t, i18n } = useTranslation();
  if (!health || !health.lastFiledMonth) {
    return <span className={styles.freshnessUnknown}>—</span>;
  }
  const months = monthsAgo(health.lastFiledMonth);
  const f = freshnessFor(months, frequency);
  const cls = f === 'recent' ? styles.freshnessFresh
            : f === 'stale' ? styles.freshnessStale
            : styles.freshnessOverdue;
  const label = formatWholeDay(health.lastFiledMonth, { month: 'short', year: 'numeric' }, i18n.resolvedLanguage ?? i18n.language ?? 'en');
  const cadence = expectedPeriodMonths(frequency);
  // Was a hard-coded English tooltip (the i18n sweep can't see a string built in a variable).
  const tooltip = t('clientsPage.freshness.tooltip', {
    label,
    ago: t('clientsPage.freshness.monthsAgo', { count: months }),
    every: t('clientsPage.freshness.everyMonths', { count: cadence }),
  });
  return <span className={cls} title={tooltip}>{label}</span>;
}

export default function StateTaxClientsPage() {
  const styles = useStyles();
  const { t } = useTranslation();
  // The CRM's filing frequency ("Monthly") in the reader's language; the stored value stays the filter key.
  const cadenceName = useCallback((f: string) => t(`clientDetailPage.history.cadenceName.${f}`, { defaultValue: f }), [t]);
  const { data, isLoading, isError, error } = useStateTaxClients();
  const { data: taxTypesByClient, isError: taxTypesError } = useObligationTaxTypesByClient();
  const { data: filingHealth, isError: filingHealthError } = useFilingHealthByClient();
  const { data: bookkeepingAccountIds, isError: bookkeepingError } = useBookkeepingAccountIds();
  // Per-client soonest non-terminal due date for the "Next due" column. Filing-timely is
  // a priority per CV — this surface lets staff sort the roster by who's most-imminent.
  // Aggregated server-side (already excludes terminal tasks) so we don't pull the whole
  // task collection to the browser; see useClientsNextDue.
  const { data: nextDueRaw, isError: nextDueError } = useClientsNextDue();
  // Per-client billing snapshot (recurring / overdue / reconciliation), cached + synced daily.
  const { data: billingSummaries, isError: billingError } = useClientBillingSummaries();
  const billingByClient = useMemo(() => {
    const m = new Map<string, ClientBilling>();
    for (const b of billingSummaries ?? []) m.set(b.stateTaxClientId, b);
    return m;
  }, [billingSummaries]);

  // Map: clientId → soonest non-terminal due date (ms since epoch), parsed from the
  // server's ISO map. Used by the column display + sort.
  const nextDueByClient = useMemo(() => {
    const out = new Map<string, number>();
    if (!nextDueRaw) return out;
    for (const [clientId, iso] of Object.entries(nextDueRaw)) {
      // The next due date is a whole day: read its own calendar day, never UTC midnight shown in Eastern (BL-142).
      const day = wholeDayLocalDate(iso);
      if (day) out.set(clientId, day.getTime());
    }
    return out;
  }, [nextDueRaw]);
  // Bookkeeping accountId membership Set — built once per fetch, used for both
  // the per-row chip and the "bookkeeping only" filter.
  const bookkeepingSet = useMemo(
    () => new Set((bookkeepingAccountIds ?? []).map(id => id.toLowerCase())),
    [bookkeepingAccountIds],
  );
  const isBookkeepingClient = (accountId: string | null | undefined) =>
    !!accountId && bookkeepingSet.has(accountId.toLowerCase());
  const [search, setSearch] = useState('');
  const [bookkeepingOnly, setBookkeepingOnly] = useState(false);
  // STX-UX-01 — Dataverse-style per-column header filters. The name column is text ("contains"). Owner, taxes, frequency and
  // source are LOOKUP columns (fleet filter contract v3 §16.8 / C86, adopted 2026-11-03): each holds the shared `LookupSelection`
  // (pick several, "Exclude these", "(Has no value)"). An ownerless client is the owner column's "(Has no value)".
  const [nameFilter, setNameFilter] = useState('');
  const [ownerSel, setOwnerSel] = useState<LookupSelection>(EMPTY_SELECTION);
  const [taxSel, setTaxSel] = useState<LookupSelection>(EMPTY_SELECTION);
  const [frequencySel, setFrequencySel] = useState<LookupSelection>(EMPTY_SELECTION);
  const [sourceSel, setSourceSel] = useState<LookupSelection>(EMPTY_SELECTION);

  // BL-118: "My clients" means owned by the signed-in staff member. Client ownership is stored by NAME (`ownerName`), so resolve the
  // user's staff row by email (the Tasks page's rule) and use its name.
  const { accounts } = useMsal();
  const { data: staff } = useSalesTaxAdmins();
  const currentUserEmail = (accounts[0]?.username ?? '').toLowerCase();
  const myName = (staff ?? []).find((s) => (s.email ?? '').toLowerCase() === currentUserEmail && !!currentUserEmail)?.name ?? null;
  // A preset's owner filter is plain "include these names"; an owner selection that excludes or asks for "(Has no value)" is custom.
  const presetOwners = !isLookupActive(ownerSel) ? []
    : !ownerSel.excluding && !ownerSel.includeNoValue ? ownerSel.codes : [CUSTOM_OWNER_SELECTION];
  const currentClientPreset = activeClientPreset(presetOwners, bookkeepingOnly, myName);
  const selectClientPreset = (key: string) => {
    const preset = CLIENT_PRESETS.find((p) => p.key === key);
    if (!preset) return;
    const next = clientPresetState(preset, myName);
    setOwnerSel({ ...EMPTY_SELECTION, codes: next.ownerFilter });
    setBookkeepingOnly(next.bookkeepingOnly);
  };
  // Default sort: lastFiled descending — clients who haven't filed recently
  // surface at the top so staff can triage. Click a header to switch.
  const [sortBy, setSortBy] = useState<SortColumn>('lastFiled');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');

  // Bulk "edit multiple" — selected client ids (Set, like the dashboard at-risk panel)
  // plus the dialog open flag.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);

  function toggleSelected(id: string, checked: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function toggleSort(col: SortColumn) {
    if (col === sortBy) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      // Sensible defaults for each column.
      setSortDir(col === 'lastFiled' ? 'desc' : 'asc');
    }
  }

  const lookupColumns = useMemo<LookupColumn<StateTaxClientSummary>[]>(() => [
    { key: 'owner', selection: ownerSel, values: (c) => (c.ownerName ? [c.ownerName] : []) },
    { key: 'taxes', selection: taxSel, values: (c) => taxTypesByClient?.[c.stateTaxClientId] ?? [] },
    { key: 'frequency', selection: frequencySel, values: (c) => (c.filingFrequencyLabel ? [c.filingFrequencyLabel] : []) },
    { key: 'source', selection: sourceSel, values: (c) => (c.locationOfInformationLabel ? [c.locationOfInformationLabel] : []) },
  ], [ownerSel, taxSel, frequencySel, sourceSel, taxTypesByClient]);

  // The rows every non-lookup filter allows (test clients, bookkeeping, name, search): the facet base (§16.3), and the list
  // before its lookup filters.
  const baseRows = useMemo(() => {
    if (!data) return [];
    // Test clients are hidden from every staff list (Christian, 2026-10-20). The task-sync
    // pilot opts in with ?showTestClients=1 — see includeTestClients.
    let rows = includeTestClients() ? data : data.filter((c) => !isTestClient(c.accountName));
    if (bookkeepingOnly) {
      rows = rows.filter((c) => bookkeepingSet.has(c.accountId.toLowerCase()));
    }
    // STX-UX-01 — the name column's "contains" filter (client-side; empty = no filter).
    const nameNeedle = nameFilter.trim().toLowerCase();
    if (nameNeedle) {
      rows = rows.filter((c) => c.accountName.toLowerCase().includes(nameNeedle));
    }
    const needle = search.trim().toLowerCase();
    if (needle) {
      rows = rows.filter((c) => {
        const taxes = taxTypesByClient?.[c.stateTaxClientId] ?? [];
        return (
          c.accountName.toLowerCase().includes(needle) ||
          (c.ownerName?.toLowerCase().includes(needle) ?? false) ||
          (c.filingFrequencyLabel?.toLowerCase().includes(needle) ?? false) ||
          (c.filingFrequencyLabel ? cadenceName(c.filingFrequencyLabel).toLowerCase().includes(needle) : false) ||
          taxes.some((tx) => tx.toLowerCase().includes(needle))
        );
      });
    }
    return rows;
  }, [data, search, taxTypesByClient, bookkeepingOnly, bookkeepingSet, nameFilter, cadenceName]);

  const filtered = useMemo(() => {
    const rows = applyLookups(baseRows, lookupColumns);

    // Sort. Compare fn returns a numeric/string-comparable value per column.
    const dirMul = sortDir === 'asc' ? 1 : -1;
    const sorted = [...rows].sort((a, b) => {
      switch (sortBy) {
        case 'client':
          return a.accountName.localeCompare(b.accountName) * dirMul;
        case 'taxes': {
          const an = (taxTypesByClient?.[a.stateTaxClientId] ?? []).length;
          const bn = (taxTypesByClient?.[b.stateTaxClientId] ?? []).length;
          return (an - bn) * dirMul;
        }
        case 'owner':
          return (a.ownerName ?? '~').localeCompare(b.ownerName ?? '~') * dirMul;
        case 'lastFiled': {
          const ak = freshnessSortKey(filingHealth?.[a.stateTaxClientId]);
          const bk = freshnessSortKey(filingHealth?.[b.stateTaxClientId]);
          return (ak - bk) * dirMul;
        }
        case 'nextDue': {
          const ak = nextDueSortKey(nextDueByClient.get(a.stateTaxClientId));
          const bk = nextDueSortKey(nextDueByClient.get(b.stateTaxClientId));
          return (ak - bk) * dirMul;
        }
      }
    });
    return sorted;
  }, [baseRows, lookupColumns, taxTypesByClient, filingHealth, nextDueByClient, sortBy, sortDir]);

  // STX-UX-01 — distinct option lists for the header filters, computed from the full
  // roster (incl. the Test Company pilot fixture), never from the filtered rows. Each option's
  // count says how many rows it would give under the other filters; a 0 is shown, dimmed.
  const realClients = useMemo(() => data ?? [], [data]);
  // An owner with no clients still belongs in the list when they are on the staff roster, so "My clients" stays reachable (dimmed
  // at 0) for a new staff member. Ownerless clients are the "(Has no value)" row.
  const ownerOptions = useMemo(() => {
    const names = new Set<string>();
    for (const c of realClients) if (c.ownerName) names.add(c.ownerName);
    for (const s of staff ?? []) if (s.name) names.add(s.name);
    return Array.from(names).sort((a, b) => a.localeCompare(b)).map((n) => ({ value: n, label: n }));
  }, [realClients, staff]);
  const taxOptions = useMemo(() => {
    const set = new Set<string>();
    for (const c of realClients)
      for (const tx of taxTypesByClient?.[c.stateTaxClientId] ?? []) set.add(tx);
    return Array.from(set).sort((a, b) => a.localeCompare(b)).map((tx) => ({ value: tx, label: tx }));
  }, [realClients, taxTypesByClient]);
  const frequencyOptions = useMemo(() => {
    const set = new Set<string>();
    for (const c of realClients) if (c.filingFrequencyLabel) set.add(c.filingFrequencyLabel);
    // The value stays the stored label (what rows carry); the option shows it in the UI language.
    return Array.from(set).sort((a, b) => a.localeCompare(b)).map((f) => ({ value: f, label: cadenceName(f) }));
  }, [realClients, cadenceName]);
  const sourceOptions = useMemo(() => {
    const set = new Set<string>();
    for (const c of realClients) if (c.locationOfInformationLabel) set.add(c.locationOfInformationLabel);
    return Array.from(set).sort((a, b) => a.localeCompare(b)).map((s) => ({ value: s, label: s }));
  }, [realClients]);

  // §16.3: each lookup column counts its options over the rows matching every OTHER filter.
  const facets = useMemo(() => ({
    owner: facetCountsFor(baseRows, lookupColumns, 'owner', ownerOptions.map((o) => o.value)),
    taxes: facetCountsFor(baseRows, lookupColumns, 'taxes', taxOptions.map((o) => o.value)),
    frequency: facetCountsFor(baseRows, lookupColumns, 'frequency', frequencyOptions.map((o) => o.value)),
    source: facetCountsFor(baseRows, lookupColumns, 'source', sourceOptions.map((o) => o.value)),
  }), [baseRows, lookupColumns, ownerOptions, taxOptions, frequencyOptions, sourceOptions]);

  const multiObligationCount = useMemo(
    () =>
      Object.values(taxTypesByClient ?? {}).filter((arr) => arr.length > 1).length,
    [taxTypesByClient],
  );

  // How many of the visible state-tax clients are also bookkeeping clients —
  // used in the header callout. Computed against the unfiltered roster so the
  // number is stable regardless of the active filter.
  const bookkeepingOverlapCount = useMemo(() => {
    if (!data) return 0;
    return data.filter(c => bookkeepingSet.has(c.accountId.toLowerCase())).length;
  }, [data, bookkeepingSet]);

  // Clients backing the current selection (id + name for the bulk dialog). Drawn from the
  // full dataset so a selection survives search/filter changes.
  const selectedClients = useMemo(() => {
    if (!data) return [];
    return data
      .filter((c) => selectedIds.has(c.stateTaxClientId))
      .map((c) => ({ id: c.stateTaxClientId, name: c.accountName }));
  }, [data, selectedIds]);

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((c) => selectedIds.has(c.stateTaxClientId));
  const someFilteredSelected =
    !allFilteredSelected && filtered.some((c) => selectedIds.has(c.stateTaxClientId));

  function toggleSelectAll(checked: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const c of filtered) {
        if (checked) next.add(c.stateTaxClientId);
        else next.delete(c.stateTaxClientId);
      }
      return next;
    });
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <Text as="h1" className={styles.title}>
            {t('clientsPage.title')}
          </Text>
          <div className={styles.subtitle}>
            {isLoading ? (
              t('common.loading')
            ) : data ? (
              <span className={styles.badgeRow}>
                <span className={styles.countCallout}>
                  <BuildingRegular /> {t('clientsPage.countClients', { count: data.length })}
                </span>
                {multiObligationCount > 0 && (
                  <span className={styles.countCallout} style={{ backgroundColor: 'rgba(255, 199, 79, 0.18)', color: 'rgb(165, 89, 0)' }}>
                    <TagMultipleRegular /> {t('clientsPage.multiObligationCount', { count: multiObligationCount })}
                  </span>
                )}
                {bookkeepingOverlapCount > 0 && (
                  <span className={styles.countCallout} style={{ backgroundColor: tokens.colorPaletteTealBackground2, color: tokens.colorPaletteTealForeground2 }}>
                    <BookRegular /> {t('clientsPage.alsoBookkeeping', { count: bookkeepingOverlapCount })}
                  </span>
                )}
              </span>
            ) : (
              t('common.loading')
            )}
          </div>
        </div>
        <div className={styles.filterRow}>
          <Field label={t('common.search')} className={styles.searchField}>
            <Input
              contentBefore={<SearchRegular />}
              value={search}
              onChange={(_, d) => setSearch(d.value)}
              placeholder={t('clientsPage.searchPlaceholder')}
            />
          </Field>
          <Switch
            checked={bookkeepingOnly}
            onChange={(_, dataArg) => setBookkeepingOnly(dataArg.checked)}
            label={t('clientsPage.bookkeepingOnly')}
            disabled={bookkeepingOverlapCount === 0}
          />
        </div>
        {/* BL-118: the active roster view is always visible (the Tasks page's pattern, BL-124). A pill is filled only when owner + bookkeeping
            match it exactly; name, taxes, frequency, source and search layer on top. */}
        <div className={styles.viewsRow}>
          <QuickViewPills
            label={t('clientsPage.views.label')}
            activeKey={currentClientPreset}
            onSelect={selectClientPreset}
            views={CLIENT_PRESETS.map((p) => ({
              key: p.key,
              label: t(`clientsPage.views.${p.key}`),
              disabled: (p.mine && !myName) || (p.bookkeeping && bookkeepingOverlapCount === 0),
              title: p.mine && !myName ? t('clientsPage.views.mineUnavailable') : undefined,
            }))}
          />
          <Text className={styles.viewsNote} data-testid="clients-view-note">
            {currentClientPreset === null
              ? t('clientsPage.views.custom')
              : t('clientsPage.views.showing', { view: t(`clientsPage.views.${currentClientPreset}`) })}
            {' · '}
            {t('clientsPage.views.opensOn', { view: t(`clientsPage.views.${DEFAULT_CLIENT_PRESET}`) })}
          </Text>
        </div>
      </div>

      {/* STX-PROD-05: a failed secondary load blanks a column; say so rather than let a blank read as "nothing". */}
      {!isError && (
        <SecondaryLoadWarning
          failed={[
            taxTypesError && 'taxTypes', filingHealthError && 'filingHealth', bookkeepingError && 'bookkeeping',
            nextDueError && 'nextDue', billingError && 'billing',
          ]}
        />
      )}
      {isError && (
        <MessageBar intent="error">
          <MessageBarBody>
            {apiErrorMessage(error, t, 'clientsPage.loadError')}
          </MessageBarBody>
        </MessageBar>
      )}

      {isLoading && <Spinner label={t('common.loading')} />}

      {!isLoading && !isError && filtered.length === 0 && (
        <div className={styles.emptyState}>
          {data && data.length === 0 ? t('clientsPage.noClients') : t('clientsPage.noMatches')}
        </div>
      )}

      {selectedIds.size > 0 && (
        <div className={styles.bulkBar}>
          <span className={styles.bulkBarCount}>
            {t('clientsPage.bulk.selected', { count: selectedIds.size })}
          </span>
          <span className={styles.bulkBarSpacer} />
          <Button appearance="primary" icon={<EditRegular />} onClick={() => setBulkOpen(true)}>
            {t('clientsPage.bulk.editMultiple')}
          </Button>
          <Button appearance="subtle" onClick={() => setSelectedIds(new Set())}>
            {t('clientsPage.bulk.clear')}
          </Button>
        </div>
      )}

      {/* STX-UX-01: keep the table (and its header filter triggers) mounted even when the
          active filters match zero rows — otherwise the only way OUT of an over-narrow
          header filter would disappear with the rows. The "no matches" empty state above
          still communicates the zero-row case. */}
      {!isLoading && !isError && (data?.length ?? 0) > 0 && (
        <div className={styles.tableCard}>
          <Table size="medium" aria-label={t('clientsPage.title')} className={styles.table}>
            <TableHeader className={styles.tableHeader}>
              <TableRow>
                <TableHeaderCell className={styles.checkboxCell}>
                  <Checkbox
                    checked={allFilteredSelected ? true : someFilteredSelected ? 'mixed' : false}
                    onChange={(_e, d) => toggleSelectAll(!!d.checked)}
                    aria-label={t('clientsPage.bulk.selectAll')}
                  />
                </TableHeaderCell>
                <TableHeaderCell className={styles.headerCell}>
                  <span className={styles.headerFlex}>
                    <SortableHeader
                      label={t('clientsPage.columns.client')}
                      column="client"
                      activeColumn={sortBy}
                      direction={sortDir}
                      onClick={() => toggleSort('client')}
                    />
                    <ColumnHeaderFilter
                      columnLabel={t('clientsPage.columns.client')}
                      value={nameFilter}
                      emptyValue=""
                      isActive={(v) => v.trim().length > 0}
                      onApply={setNameFilter}
                    >
                      {/* Inside a popover, so it STAGES (I5). C40's carve-out is for live search
                          OUTSIDE popovers only. */}
                      {(draft, setDraft) => (
                        <Field label={t('clientsPage.filters.nameContains')}>
                          <Input size="small" value={draft} onChange={(_, d) => setDraft(d.value)} />
                        </Field>
                      )}
                    </ColumnHeaderFilter>
                  </span>
                </TableHeaderCell>
                <TableHeaderCell className={styles.headerCell}>
                  <span className={styles.headerFlex}>
                    <SortableHeader
                      label={t('clientsPage.columns.taxesReported')}
                      column="taxes"
                      activeColumn={sortBy}
                      direction={sortDir}
                      onClick={() => toggleSort('taxes')}
                    />
                    <LookupColumnFilter
                      columnLabel={t('clientsPage.columns.taxesReported')}
                      options={taxOptions}
                      value={taxSel}
                      onApply={setTaxSel}
                      facetCounts={facets.taxes}
                    />
                  </span>
                </TableHeaderCell>
                <TableHeaderCell className={styles.headerCell}>
                  <SortableHeader
                    label={t('clientsPage.columns.lastFiled')}
                    column="lastFiled"
                    activeColumn={sortBy}
                    direction={sortDir}
                    onClick={() => toggleSort('lastFiled')}
                  />
                </TableHeaderCell>
                <TableHeaderCell className={styles.headerCell}>
                  <SortableHeader
                    label={t('clientsPage.columns.nextDue')}
                    column="nextDue"
                    activeColumn={sortBy}
                    direction={sortDir}
                    onClick={() => toggleSort('nextDue')}
                  />
                </TableHeaderCell>
                <TableHeaderCell className={styles.headerCell}>
                  <span className={styles.headerFlex}>
                    <SortableHeader
                      label={t('clientsPage.columns.owner')}
                      column="owner"
                      activeColumn={sortBy}
                      direction={sortDir}
                      onClick={() => toggleSort('owner')}
                    />
                    <LookupColumnFilter
                      columnLabel={t('clientsPage.columns.owner')}
                      options={ownerOptions}
                      value={ownerSel}
                      onApply={setOwnerSel}
                      facetCounts={facets.owner}
                    />
                  </span>
                </TableHeaderCell>
                <TableHeaderCell className={styles.headerCell}>
                  <span className={styles.headerFlex}>
                    {t('clientsPage.columns.filingFrequency')}
                    <LookupColumnFilter
                      columnLabel={t('clientsPage.columns.filingFrequency')}
                      options={frequencyOptions}
                      value={frequencySel}
                      onApply={setFrequencySel}
                      facetCounts={facets.frequency}
                    />
                  </span>
                </TableHeaderCell>
                <TableHeaderCell className={styles.headerCell}>
                  <span className={styles.headerFlex}>
                    {t('clientsPage.columns.informationSource')}
                    <LookupColumnFilter
                      columnLabel={t('clientsPage.columns.informationSource')}
                      options={sourceOptions}
                      value={sourceSel}
                      onApply={setSourceSel}
                      facetCounts={facets.source}
                      align="end"
                    />
                  </span>
                </TableHeaderCell>
                <TableHeaderCell className={styles.headerCell}>
                  {t('clientsPage.columns.notes')}
                </TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => {
                const taxes = taxTypesByClient?.[c.stateTaxClientId] ?? [];
                const initials = ownerInitials(c.ownerName);
                return (
                  <TableRow
                    key={c.stateTaxClientId}
                    className={mergeClasses(styles.clickableRow, ROW_LINK_HOST)}
                  >
                    <TableCell
                      className={styles.checkboxCell}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={selectedIds.has(c.stateTaxClientId)}
                        onChange={(_e, d) => toggleSelected(c.stateTaxClientId, !!d.checked)}
                        aria-label={t('clientsPage.bulk.selectRow', { name: c.accountName })}
                      />
                    </TableCell>
                    <TableCell>
                      <TableCellLayout>
                        <div className={styles.clientNameStack}>
                          <RowLink href={`/clients/${c.stateTaxClientId}`} className={mergeClasses(styles.rowLinkText, styles.clientCell)}>
                            {c.accountName || t('clientsPage.unnamedAccount')}
                          </RowLink>
                          {(() => {
                            // One row of small badges under the name: Bookkeeping, then billing. The chip used to be its own
                            // full-width line that stretched to the name's width, so its size changed row to row.
                            const b = billingByClient.get(c.stateTaxClientId);
                            const bookkeeping = isBookkeepingClient(c.accountId);
                            const billing = !!b && b.xeroLinked;
                            if (!bookkeeping && !billing) return null;
                            return (
                              <span className={styles.badgeRow}>
                                {bookkeeping && (
                                  <Badge
                                    appearance="tint"
                                    color="brand"
                                    size="small"
                                    icon={<BookRegular />}
                                    title={t('clientsPage.bookkeepingChipTooltip')}
                                    data-testid="bookkeeping-chip"
                                  >
                                    {t('clientsPage.bookkeepingChip')}
                                  </Badge>
                                )}
                                {billing && b && (<>
                                <Badge
                                  appearance="tint"
                                  color={b.recurringActive ? 'success' : 'subtle'}
                                  size="small"
                                  title={t('clientsPage.billing.recurringTooltip')}
                                >
                                  {b.recurringActive
                                    ? t('clientsPage.billing.recurring')
                                    : t('clientsPage.billing.noRecurring')}
                                </Badge>
                                {b.overdue.count > 0 && (
                                  <Badge
                                    appearance="tint"
                                    color="danger"
                                    size="small"
                                    title={t('clientsPage.billing.overdueTooltip', {
                                      days: b.overdue.oldestDaysOverdue,
                                    })}
                                  >
                                    {t('clientsPage.billing.overdue', { count: b.overdue.count })}
                                  </Badge>
                                )}
                                {b.reconciliation.status === 'gaps' && (
                                  <Badge
                                    appearance="tint"
                                    color="warning"
                                    size="small"
                                    title={t('clientsPage.billing.gapTooltip', {
                                      list: b.reconciliation.unbilledObligations.join(', '),
                                    })}
                                  >
                                    {t('clientsPage.billing.gap')}
                                  </Badge>
                                )}
                                </>)}
                              </span>
                            );
                          })()}
                        </div>
                      </TableCellLayout>
                    </TableCell>
                    <TableCell>
                      {taxes.length === 0 ? (
                        <span className={styles.unconfiguredBadge}>
                          {t('common.notConfigured')}
                        </span>
                      ) : (
                        <div className={styles.badgeRow}>
                          {taxes.map((tx) => (
                            <Badge
                              key={tx}
                              appearance="tint"
                              color={badgeColorForTaxType(tx)}
                              size="small"
                              title={tx}
                            >
                              {shortTaxLabel(tx)}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <FreshnessBadge
                        health={filingHealth?.[c.stateTaxClientId]}
                        frequency={c.filingFrequencyLabel}
                      />
                    </TableCell>
                    <TableCell>
                      <NextDueBadge
                        dueMs={nextDueByClient.get(c.stateTaxClientId)}
                        dash={t('common.dash')}
                        t={t}
                      />
                    </TableCell>
                    <TableCell>
                      <div className={styles.ownerCell}>
                        {initials ? (
                          <>
                            <Avatar
                              size={24}
                              name={c.ownerName ?? undefined}
                              initials={initials}
                              color="colorful"
                            />
                            <span className={styles.ownerName}>{c.ownerName}</span>
                          </>
                        ) : (
                          <span className={styles.mutedCell}>{t('common.unassigned')}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <TableCellLayout>
                        {c.filingFrequencyLabel ? (
                          <Badge appearance="outline" color="informative" size="small">
                            {cadenceName(c.filingFrequencyLabel)}
                          </Badge>
                        ) : (
                          <span className={styles.mutedCell}>—</span>
                        )}
                      </TableCellLayout>
                    </TableCell>
                    <TableCell>
                      <TableCellLayout>
                        {c.locationOfInformationLabel ?? <span className={styles.mutedCell}>—</span>}
                      </TableCellLayout>
                    </TableCell>
                    <TableCell>
                      <span className={styles.notesCell} title={c.notes ?? undefined}>
                        {c.notes ?? <span className={styles.mutedCell}>—</span>}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <BulkEditClientsDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        selectedClients={selectedClients}
        onCommitted={() => setSelectedIds(new Set())}
      />
    </div>
  );
}
